/**
 * Phase 6b2 PR 2 — `<ConfirmDialog>` (team variant).
 *
 * Per safety gate #7, every destructive team action (T7 remove
 * athlete · T8 leave team) routes through this dialog. The 6b1
 * `<DeleteConfirmDialog>` is delete-specific in its locked warning
 * copy ("This can't be undone."); team destructive flows use a
 * different framing — direction.md §2 locks the copy to:
 *
 *   - T7 remove: "Remove {athlete name}?" +
 *     "Their race history stays on their profile."
 *   - T8 leave:  "Leave {team name}?" +
 *     "Your race history stays on your profile."
 *
 * This component is shape-compatible with `<DeleteConfirmDialog>` but
 * accepts custom body copy + confirm-button label so the team flows
 * carry their own locked-vanilla framing.
 *
 * Focus / keyboard / focus-trap behavior mirrors
 * `<DeleteConfirmDialog>` exactly:
 *   - Default focus = Cancel (never Confirm)
 *   - ESC dismisses
 *   - Tab cycles within dialog
 *   - Backdrop click dismisses
 *
 * Both `modal` (desktop) and `sheet` (mobile) variants ship in the
 * same component, chosen by the caller via the `variant` prop based
 * on `useMediaQuery('(min-width: 1024px)')`.
 */

import { useEffect, useRef } from 'react';
import styles from './ConfirmDialog.module.css';

export interface ConfirmDialogProps {
  title: string;
  /** Athlete-readable lossless-delete framing. e.g. "Their race
   *  history stays on their profile." Caller picks the copy. */
  body: string;
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". W2-CHROME: the sign-out
   *  question names its default choice ("Stay signed in"); every other
   *  caller omits this and is unchanged. */
  cancelLabel?: string;
  /** Confirm handler. The caller fires the optimistic write +
   *  refetch. */
  onConfirm: () => void;
  /** Cancel / dismiss. */
  onClose: () => void;
  /** 'modal' (desktop) or 'sheet' (mobile). */
  variant: 'modal' | 'sheet';
  /** 'danger' (default, byte for byte what the team flows have always drawn: the
   *  3 px red top rule and the red confirm) or 'neutral' (W2 Claude Design item 8:
   *  no top rule, a marine confirm; for a question that is not destructive, like
   *  "Sign out of Tayro?"). Copy, focus, Escape and backdrop do not depend on it. */
  tone?: 'danger' | 'neutral';
  /** If true, set inert + spinner on the confirm button while the
   *  write is in flight. */
  pending?: boolean;
  /** Optional inline error chip rendered above the action row when
   *  the write fails. Caller surfaces the server's message. */
  errorMessage?: string | null;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onClose,
  variant,
  tone = 'danger',
  pending = false,
  errorMessage,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Default focus = Cancel. NEVER Confirm — accidental Enter must
  // not fire a destructive action.
  useEffect(() => {
    const t = setTimeout(() => cancelRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  // ESC dismisses; Tab cycles within dialog (focus trap).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (pending) return; // ignore keyboard while write is in flight
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, pending]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (pending) return;
    if (e.target === e.currentTarget) onClose();
  };

  const isSheet = variant === 'sheet';
  // The danger tone adds nothing to the markup: its class string is exactly what
  // it was before `tone` existed.
  const containerClass = `${isSheet ? styles.sheetContainer : styles.modalContainer}${
    tone === 'neutral' ? ` ${styles.neutral}` : ''
  }`;

  return (
    <div
      className={isSheet ? styles.sheetBackdrop : styles.modalBackdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={containerClass}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="team-confirm-title"
        aria-describedby="team-confirm-body"
      >
        {isSheet && <div className={styles.handle} aria-hidden="true" />}
        <h2 id="team-confirm-title" className={styles.title}>
          {title}
        </h2>
        <p id="team-confirm-body" className={styles.body}>
          {body}
        </p>
        {errorMessage && (
          <p className={styles.errorChip} role="alert">
            {errorMessage}
          </p>
        )}
        <div className={isSheet ? styles.sheetActions : styles.modalActions}>
          <button
            ref={cancelRef}
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={pending}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={onConfirm}
            disabled={pending}
            aria-busy={pending || undefined}
          >
            {pending ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
